import { useEffect, useRef } from 'react'
import { useIsomorphicLayoutEffect } from '@react-hookz/web'

interface SnackbarTimeoutHookParams {
  permanent: boolean
  retained: boolean
  onClose(): void
}

export const useSnackbarTimeout = (
  timeout: number,
  { permanent, retained, onClose }: SnackbarTimeoutHookParams,
): void => {
  const isPaused = permanent || retained

  const onCloseRef = useRef(onClose)

  useIsomorphicLayoutEffect(() => {
    onCloseRef.current = onClose
  })

  const remainingTimeoutRef = useRef(timeout)

  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const timeoutStartRef = useRef<number | null>(null)

  const previousPausedRef = useRef(isPaused)
  const previousTimeoutRef = useRef(timeout)

  const clearActiveTimeout = () => {
    if (timeoutIdRef.current === null) {
      return
    }

    clearTimeout(timeoutIdRef.current)
    timeoutIdRef.current = null
    timeoutStartRef.current = null
  }

  const startTimeout = () => {
    if (remainingTimeoutRef.current <= 0) {
      onCloseRef.current()
      return
    }

    timeoutStartRef.current = Date.now()
    timeoutIdRef.current = setTimeout(() => onCloseRef.current(), remainingTimeoutRef.current)
  }

  useEffect(() => {
    if (timeout < 0) {
      clearActiveTimeout()
      previousPausedRef.current = isPaused
      previousTimeoutRef.current = timeout
      return
    }

    const wasPaused = previousPausedRef.current
    const previousTimeout = previousTimeoutRef.current

    previousPausedRef.current = isPaused
    previousTimeoutRef.current = timeout

    if (isPaused) {
      if (!wasPaused && timeoutIdRef.current !== null && timeoutStartRef.current !== null) {
        const elapsedTime = Date.now() - timeoutStartRef.current

        remainingTimeoutRef.current = Math.max(0, remainingTimeoutRef.current - elapsedTime)
      }

      if (timeout !== previousTimeout) {
        remainingTimeoutRef.current = timeout
      }

      clearActiveTimeout()
      return
    }

    if (wasPaused) {
      startTimeout()
      return
    }

    if (timeout !== previousTimeout) {
      remainingTimeoutRef.current = timeout
      clearActiveTimeout()
      startTimeout()
      return
    }

    if (timeoutIdRef.current === null) {
      remainingTimeoutRef.current = timeout
      startTimeout()
    }
  }, [isPaused, timeout])

  useEffect(() => clearActiveTimeout, [])
}
